import { useEffect, useState } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

/* ---------------------------
   Types — contrat de GET /v1/market/fear-greed (api/app/fear_greed/service.py::lire_indice)
   --------------------------- */
export type FearGreedZone = 'PEUR_EXTREME' | 'PEUR' | 'NEUTRE' | 'AVIDITE' | 'AVIDITE_EXTREME';

export interface FearGreedPoint {
    date: string;
    score: number;
    zone: FearGreedZone;
}

export interface FearGreedComposante {
    cle: string;
    libelle: string;
    description: string;
    brut: number | null;
    score: number | null;
}

export interface FearGreedData {
    date: string;
    score: number;
    zone: FearGreedZone;
    references: {
        veille: FearGreedPoint | null;
        semaine: FearGreedPoint | null;
        mois: FearGreedPoint | null;
        haut_1an: FearGreedPoint | null;
        bas_1an: FearGreedPoint | null;
    };
    composantes: FearGreedComposante[];
    historique: { date: string; score: number }[];
}

export interface FearGreedMeta {
    methodo_version: string;
    nb_titres_univers?: number;
    source?: string;
    avertissement?: string;
    message?: string;
}

interface FearGreedResponse {
    data: FearGreedData | null;
    meta: FearGreedMeta;
}

/**
 * Indice Peur & Avidité Kanea. `enabled` à false : aucune requête (phase de rodage,
 * l'endpoint est réservé à `admin_platform` — un non-admin recevrait un 403).
 */
export function useFearGreed(enabled: boolean, jours = 365) {
    const { token } = useAuth();
    const [data, setData] = useState<FearGreedData | null>(null);
    const [meta, setMeta] = useState<FearGreedMeta | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!enabled || !token) return;
        let annule = false;
        setLoading(true);
        setError(null);
        fetch(`${API_URL}/v1/market/fear-greed?jours=${jours}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(async res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const body: FearGreedResponse = await res.json();
                if (!annule) {
                    setData(body.data);
                    setMeta(body.meta);
                }
            })
            .catch(e => {
                if (!annule) setError(e instanceof Error ? e.message : String(e));
            })
            .finally(() => {
                if (!annule) setLoading(false);
            });
        return () => {
            annule = true;
        };
    }, [enabled, token, jours]);

    return { data, meta, loading, error };
}
